import { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  ColumnDef,
  flexRender,
} from '@tanstack/react-table';
import { useContext } from 'react';
import { ThemeContext } from '@/contexts/ThemeContext';
import { MoreHorizontal, Pencil, Trash, PlusCircle, Plus } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Plan } from '../types';
import { usePlanStore } from '@/stores/planStore';
import { getColorStyle, PlanColorIndex } from '@/utils/colors';
import { formatCurrency } from '@/utils/currencyFormatter';
import { useProviderCurrency } from '@/stores/currencyStore';
import { Badge } from '@/components/ui/badge';

interface PlanTableProps {
  plans: Plan[];
  onEdit: (plan: Plan) => void;
  onDelete: (plan: Plan) => void;
  isReadOnly?: boolean;
}

export function PlanTable({ plans, onEdit, onDelete, isReadOnly }: PlanTableProps) {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';
  const { setSelectedPlan } = usePlanStore();
  const providerId = plans.length > 0 ? plans[0].provider_id : 0;
  const currency = useProviderCurrency(providerId);

  // Calculate total unique channels and assignments
  const totalUniqueChannels = plans.reduce((uniqueChannels, plan) => {
    const channelIds = new Set([...plan.channels.map(c => c.id)]);
    return new Set([...uniqueChannels, ...channelIds]);
  }, new Set<number>()).size;

  const totalAssignments = plans.reduce((total, plan) => total + plan.channel_count, 0);

  const columns = useMemo<ColumnDef<Plan>[]>(
    () => [
      {
        id: 'color',
        cell: ({ row }) => (
          <div
            className="w-4 h-4 rounded-full"
            style={getColorStyle(row.original.color_index as PlanColorIndex, isDark)}
          />
        ),
        size: 50,
      },
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => (
          <div className="max-w-[300px] truncate">
            {row.original.description || '-'}
          </div>
        ),
      },
      {
        accessorKey: 'is_addon',
        header: 'Type',
        cell: ({ row }) => (
          row.original.is_addon ? (
            <Badge variant="destructive" className="flex-shrink-0">
              Add-on
            </Badge>
          ) : (
            <Badge variant="secondary" className="flex-shrink-0">
              Regular
            </Badge>
          )
        ),
      },
      {
        accessorKey: 'monthly_price',
        header: 'Monthly Price',
        cell: ({ row }) => {
          const price = row.original.monthly_price;
          return formatCurrency(price, currency);
        },
      },
      {
        accessorKey: 'yearly_price',
        header: 'Yearly Price',
        cell: ({ row }) => {
          const price = row.original.yearly_price;
          return formatCurrency(price, currency);
        },
      },
      {
        accessorKey: 'channel_count',
        header: 'Channels',
        cell: ({ row }) => row.original.channel_count,
        footer: () => (
          <div className="text-right font-medium">
            <div>Total Assignments: {totalAssignments}</div>
            <div>Unique Channels: {totalUniqueChannels}</div>
          </div>
        ),
      },
      {
        id: 'actions',
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => onEdit?.(row.original)}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onDelete?.(row.original)}
                className="text-destructive"
              >
                <Trash className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [isDark, onEdit, onDelete, totalAssignments, totalUniqueChannels, currency]
  );

  const table = useReactTable({
    data: plans,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                onClick={() => setSelectedPlan(row.original)}
                className="cursor-pointer"
                data-state={row.original.id === plans.find(p => p.id)?.id && "selected"}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                <div className="flex flex-col items-center justify-center">
                  <PlusCircle className="h-10 w-10 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold">No Plans Available</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Get started by adding your first plan
                  </p>
                  {!isReadOnly && (
                    <Button
                      onClick={() => usePlanStore.getState().openCreateModal()}
                      variant="outline"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Plan
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
} 